/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2018, 2021
 *|
*| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restrictedashboardSpecSvcd by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'underscore',
	'./BaseSmartsVisRecommenderWrapper',
	'./IRUtils',
	'../../apiHelpers/SlotAPIHelper',
	'../../widgets/livewidget/nls/StringResources',
	'./LegacyMapMappingManager',
	'./SmartsRecommenderError',
	'./VisRecommenderBindingFallback',
	'../../extensions/smarts-recommender/impl/Utils'
], function (
	_,
	BaseSmartsVisRecommenderWrapper,
	IRUtils,
	SlotAPIHelper,
	StringResources,
	LegacyMapMappingManager,
	SmartsError,
	VisRecommenderBindingFallback,
	Utils
) {
	'use strict';

	/*
	* This class is a wrapper for the Smarts Visualization Recommender for dashboard.
	*/
	var DashboardSmartsVisRecommenderWrapper = BaseSmartsVisRecommenderWrapper.extend({

		_UnsupportedVisIds: ['list', 'dataPlayer', 'com.ibm.vis.rave2polygonmap'], // not supported by SmartsVisRecommender
		_LegacyMapMappingManager: LegacyMapMappingManager, //for unit tests
		_VisRecommenderBindingFallback: VisRecommenderBindingFallback,
		// constructor
		init: function ({ moserDataSources, visDefinitions, logger, dashboardApi, dataSources }) {

			this._visDefinitions = visDefinitions;
			this._logger = logger;
			this._dashboardApi = dashboardApi;

			const ajaxSvc = dashboardApi.getGlassCoreSvc('.Ajax');
			let glassOptions = {};
			dashboardApi.prepareGlassOptions(glassOptions);

			DashboardSmartsVisRecommenderWrapper.inherited('init', this, [{ moserDataSources, ajaxSvc, logger, glassOptions, dashboardApi, dataSources }]);
		},

		destroy: function () {
			DashboardSmartsVisRecommenderWrapper.inherited('destroy', this, arguments);
			this.moserDataSources = null;
			this._visDefinitions = null;
			this._logger = null;
			this._dashboardApi = null;
		},

		/**
		 * Fetch the recommender information for the current chart
		 * @return {object} payload from smarts server (including `label` attribute)
		 */
		getRecommendInfo: function(visualization) {
			const dataSourceAPI = visualization.getDataSource();
			if(!dataSourceAPI){
				return Promise.resolve({});
			}else{
				return dataSourceAPI.loadMetadata().then(() => {
					const columns = this._getBoundColumnsInfo(visualization);
					this._replaceHiddenCustomGroupCols(columns, visualization);
					const options = this._getRequestOptions(visualization);
					const definition = visualization.getDefinition();
					return DashboardSmartsVisRecommenderWrapper.inherited('getRecommendInfo', this, [{
						columns: columns,
						visId: definition ? definition.getId() : null,
						requestOptions: options
					}]).then(chartInfo => {
						if (chartInfo.mandatorySlotsMissing) {
							return this._buildChartInfoFromColumns(columns, options.module);
						}
						return chartInfo;
					}).catch((error) => {
						if (error.message === this.getUnsupportedVizErrCode()) {
							return this._buildChartInfoFromColumns(columns, options.module);
						} else {
							return Promise.reject(error);
						}
					});
				});
			}
		},

		_replaceHiddenCustomGroupCols: function( columns, visualization ) {
			const sourceId = visualization.getDataSource().getId();
			const shaping = this._dashboardApi.getFeature('DataSources.moser');
			if (shaping && shaping.isConsumerGroupColumn) {
				columns.forEach( (columnEntry) => {
					const columnIds = columnEntry.columnIds;
					columnIds.forEach( (colId, i) => {
						if (shaping.isConsumerGroupColumn(sourceId, colId)) {
							const customGroup = shaping.getCustomGroupData(sourceId, colId);
							if (customGroup && customGroup.basedOnMoserObjectId) {
								columnIds[i] = customGroup.basedOnMoserObjectId;
							}
						}
					} );
				} );
			}
		},

		/*
		 * This works as a local fallback to get smart title for smarts-unsupported visualization types (such as list, legacy map etc)
		 * By design, we will use the concatenated column labels as the 'smarts title'
		 */
		_buildChartInfoFromColumns: function(columns, module) {
			const aTitle =[];
			if (columns && columns.length > 0 && module) {
				columns.forEach((column) => {
					column.columnIds.forEach(id => {
						module.getMetadataColumn(id) && aTitle.push(module.getMetadataColumn(id).getLabel());
					});
				});
			}
			const joinedTitles = aTitle.join(`${StringResources.get('listSeparator')} `);
			//This could be augmented to create descriptions
			return Promise.resolve({
				label: joinedTitles,
				title: joinedTitles
			});
		},

		/*
		* Given a visualization type, recommends the binding for the newly added columns (user added new columns to locked viz type)
		* @param {string} visId
		* @param {object} array of metadataColumn ids for the newly added columns
		* @returns {Object}
		*/
		recommendBindingsForNewColumns: function(visualization, columnsToBind){
			const visId = visualization.getDefinition().getId();

			if( this._isUnsupportedVis(visualization, visId)){
				return this._recommendBindingsForUnsupportedVisId(visualization, visId, columnsToBind);
			}

			return new Promise(resolve => {
				const boundColumnsInfo = this._getBoundColumnsInfo(visualization);
				const requestOptions = this._getRequestOptions(visualization);

				resolve({
					columnsToBind,
					boundColumnsInfo,
					requestOptions,
					visId
				});
			})
				.then( this.recommendBindings.bind(this) )
				.then( this._handleBindingRecommendationResponse.bind(this, visualization))
				.catch(this._handleSmartsError.bind(this));
		},

		/*
		* Recommend bindings (aka mappings) for the newly selected visualization (user chose to change viz type) based on columns in the current model.
		* @param {string} visId
		* @returns {Object}
		*/
		recommendBindingsForSelectedViz: function(visualization, visId){

			if( this._isUnsupportedVis(visualization, visId)){
				return this._recommendBindingsForUnsupportedVisId(visualization, visId);
			}

			const requestParams = {
				visId: visId,
				requestOptions: this._getRequestOptions(visualization)
			};
			const definition = visualization.getDefinition();
			const originalVisId = definition ? definition.getId(): null;
			if( this._isUnsupportedVis(visualization, originalVisId) ) {
				requestParams.columnsToBind = Utils.getUnboundColumnsIds(visualization);
			}else{
				requestParams.originalVisId = originalVisId;
				requestParams.boundColumnsInfo = this._getBoundColumnsInfo(visualization);
			}
			return this.recommendBindings(requestParams)
				.then(this._handleBindingRecommendationResponse.bind(this, visualization))
				.catch(this._handleSmartsError.bind(this));
		},

		/*
		* Returns the best alternate visualization recommendation for the columns in the current model plus
		* new columns added (if any).
		* @param {newColumns} - optional
		* @returns {Object}
		*/
		recommendBestAlternateVisualization: function(visualization, newColumnsIds = []){
			return new Promise(resolve => {
				const columnIds = newColumnsIds.concat( Utils.getUnboundColumnsIds(visualization) );

				resolve( columnIds );
			})
				.then( columnIds => this.recommendAlternateVisualizations(columnIds, this._getRequestOptions(visualization)))
				.then( recommendations => recommendations[0])
				.catch(this._handleSmartsError.bind(this));
		},
		/*
		 * Assign the slots for visualizations that are not supported by Smarts visRecommender
		 */
		_recommendBindingsForUnsupportedVisId: function(visualization, visId, newColumnsIds){

			switch (visId){
				case 'com.ibm.vis.rave2polygonmap':{
					return this._recommendBindingsForLegacyMap(visualization, visId, newColumnsIds);
				}
				case 'dataPlayer' :
				case 'list' :{
					return this._recommendBindingsForList_DataPlayer(visualization, visId, newColumnsIds);
				}
				default: {
					return Promise.reject(new Error('Smarts does not support visualization of type ' + visualization.getDefinition().getType() + '.'));
				}
			}
		},

		_handleSmartsError: function(e) {
			this._logger.warn('An error occurred in the smart recommender', e);
			throw e;
		},

		_handleBindingRecommendationResponse: function( visualization, recommendations ){
			/*
			* Recommender may return returns > 1 binding recommendations and the recommendation may contain
			* unsupported slots or recommendations that do not render in Dashboard/Explore due to required slots not being mapped
			* This function filters out the recommendations that contains unsupported slots and pick the best recommendation based on max
			* required slots mapped - this will reduce the chance that the visualization is not rendered.
			 */

			//This is a binding request so the visId for all the recommendations are the same, so just use the first one.
			return this._getVisDefinition( recommendations[0].visId )
				.then( visDefinition => {
					//filter out the hidden slots so we dont recommend them
					const slotDefinitions = visDefinition.getSlotList().filter(slot =>{
						return !slot.getProperty('hidden');
					});
					const allSlotIds = slotDefinitions.map(dataSlot => dataSlot.getId());

					const allSlotsAreSupported = recommendation => (Object.keys(recommendation.slots).filter( slotId => allSlotIds.indexOf(slotId) === -1).length === 0);

					const requiredSlotIds = visDefinition.getSlotList().filter( dataSlot => !dataSlot.getProperty('optional') ).map( dataSlot => dataSlot.getId() );
					// returns the recommendation that has the max required slots for the viz
					const maxRequiredSlotsMatched = (accumulator, currentRec) => {
						const maxRequiredSlots = recommendation => Object.keys(recommendation.slots).filter( slotId => requiredSlotIds.indexOf( slotId ) !== -1 ).length;
						if(maxRequiredSlots(currentRec) > maxRequiredSlots(accumulator)){
							return currentRec;
						}
						return accumulator;
					};

					var recommendation = recommendations.filter(allSlotsAreSupported).reduce(maxRequiredSlotsMatched);
					//attempt to manually assign bindings for columns that smarts was unable to bind and show a toast for any that cannot be bound
					return this._handleUnboundColumns(visualization, recommendation, slotDefinitions);
				});
		},


		_getRequestOptions: function(visualization){
			const module = this._getModule(visualization);
			return {
				assetId : module.getAssetId(),
				sourceType: module.getSourceType(),
				module: module
			};
		},

		_getVisDefinition: function(visId){
			return Promise.resolve(this._visDefinitions.getById(visId));
		},

		//Return an array of column info with column and (SmartsVisRecommender) slot ids
		_getBoundColumnsInfo: function(visualization){
			const mappedSlots = visualization.getSlots().getMappedSlotList();

			if( !mappedSlots || !mappedSlots.length){
				return;
			}

			// ignore hidden slots which are not considered as part of mappings
			// e.g. 'rank' slot of grid, 'size' slot of scatter
			const columnInfos = mappedSlots.filter(slot => slot && !slot.getDefinition().isHidden())
				.map( mappedSlot => {
					const columnIds = Utils.filterInvalidDataItems(mappedSlot.getDataItemList())
						.map( dataItem => dataItem.getColumnId());

					let columnInfo;
					if (columnIds.length) {
						columnInfo = {
							slotId: mappedSlot.getId(),
							columnIds: columnIds
						};
					}
					return columnInfo;
				});
			return _.filter(columnInfos, columnInfo => columnInfo);
		},

		/*
		* Shows the toast for dropped columns.  Takes a list of column metadata as argument.
		*/
		_showToastForDroppedColumns: function(messageId, droppedColumns ){
			if( !droppedColumns || !droppedColumns.length ){
				return;
			}
			let columnLabels = droppedColumns.map( column => column.getLabel()).join(', ').toLocaleString();
			var toastInfoText = StringResources.get( messageId, { columns: columnLabels });
			this._dashboardApi.showToast(toastInfoText, { type:'info', preventDuplicates: true });
		},


		_getUsedMetadataColumns: function(visualization) {
			const columns = [];
			visualization.getSlots().getMappingInfoList().forEach(mapping => {
				if (mapping.slot){
					columns.push(mapping.dataItem.getColumnId());
				}
			});
			return columns;
		},

		_recommendBindingsForLegacyMap: function(visualization, visId, newColumnsIds){
			var bKeepExistingMappings = true;
			if( !newColumnsIds ){
				//drop existing mapping if user is changing visualization
				bKeepExistingMappings = false;
				newColumnsIds = this._getUsedMetadataColumns(visualization);
			}

			const columns = newColumnsIds.map(columnId => visualization.getDataSource().getMetadataColumn(columnId) );

			return this._getVisDefinition(visId)
				.then(function(visDefinition){
					var legacyMapMappingManager = new this._LegacyMapMappingManager(visualization);
					var mappedColumns = legacyMapMappingManager.mapColumnsToSlots(visDefinition, columns, bKeepExistingMappings);
					if( legacyMapMappingManager.getUnmappedColumns() ){
						this._showToastForDroppedColumns('maxColumnsExceeded', legacyMapMappingManager.getUnmappedColumns());
					}
					return {
						slots:mappedColumns,
						visId: visDefinition.getId()
					};
				}.bind(this));
		},

		_recommendBindingsForList_DataPlayer: function(visualization, visId, newColumnsIds){
			//The mapping of slots for both vis types is identical as they both support only one slot and any data type
			return this._getVisDefinition(visId)
				.then(function(visDefinition){

					var metadataColumns = this._getUsedMetadataColumns(visualization);
					if (newColumnsIds){
						metadataColumns = metadataColumns.concat(newColumnsIds);
					}
					// If the number of columns exceeded the max allowed for slot, either
					// a. in the case of change vis type - drop enough columns to meet the max no. of columns limit or
					// b. in the case of add columns - add enough columns to equal the max no. of columns limit and drop the rest
					var droppedColumns;
					const maxItem = visDefinition.getSlotList()[0].getProperty('maxItems');
					if( metadataColumns.length > maxItem ){
						droppedColumns = metadataColumns.splice(maxItem);
					}
					if(  droppedColumns && droppedColumns.length > 0){
						this._showToastForDroppedColumns('maxColumnsExceeded', droppedColumns.map(id => visualization.getDataSource().getMetadataColumn(id)));
					}

					const recommendation =  {
						slots:{},
						visId: visDefinition.getId()
					};
					recommendation.slots[visDefinition.getSlotList()[0].getId()] = metadataColumns;
					return recommendation;

				}.bind(this));
		},

		_isUnsupportedVis: function(visualization, visId = null){
			const definition = visualization.getDefinition();
			const isSchematic = definition && definition.getProperty('isSchematic');
			const isCustomVis = definition && definition.getProperty('isCustomVis');
			if (isSchematic || isCustomVis) {
				return true;
			}
			if (visId === null) {
				visId = definition.getId();
			}
			return this._UnsupportedVisIds.indexOf( visId) !== -1;
		},



		_handleUnboundColumns: function(visualization, recommendation, slotDefinitions){
			var unboundColumnIds = recommendation.unbound;
			if(!unboundColumnIds || !unboundColumnIds.length){
				return recommendation;
			}
			var VisRecommenderBindingFallback = new this._VisRecommenderBindingFallback(visualization);
			recommendation = VisRecommenderBindingFallback.assignUnboundColumns(recommendation, slotDefinitions);
			//show toast for columns still unable to bind
			let label = recommendation.unbound.length > 1 ? 'unboundColumns' : 'unboundColumn';
			const dataSource = visualization.getDataSource();
			this._showToastForDroppedColumns(label, recommendation.unbound.map(columnId => dataSource.getMetadataColumn(columnId)));
			return recommendation;
		},

		_getModule: function(visualization) {
			//should not cache module because it might be undefined when the viz has no mappings
			let module;
			const dataSource = visualization.getDataSource();
			if (dataSource){
				module = this.moserDataSources.getModule(dataSource.getId());
			}
			return module;
		}

	});

	return DashboardSmartsVisRecommenderWrapper;
});
